import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, User, LogOut } from 'lucide-react'
import { useBasePath } from '../hooks/useBasePath'

interface SidebarUserMenuProps {
  /** Full display name (e.g. "Tom S." or "Hassam Hassan"). Falls back to a
   * generic label if omitted. */
  displayName?: string | null
  /** Email shown in the popover header and under the sidebar name. */
  email?: string | null
  /** Optional first name — used to compose initials when both are provided. */
  firstName?: string | null
  /** Optional last name — used to compose initials when both are provided. */
  lastName?: string | null
}

/**
 * Sidebar user card with a ⋮ menu that opens a popover containing:
 *   - Repeats the user avatar + name + email as a header.
 *   - "Account" row — navigates to `<basePath>/profile`.
 *   - "Sign out" row — navigates to `/auth/logout`.
 *
 * Replaces the previous full-width "Sign out" button + separate "Profile"
 * nav item (DEV-48214 §12.3). Popover is hand-rolled to match the mockup
 * pattern at `octopus-mock/app/admin/layout.tsx:165-213` — outside-click and
 * Escape both close the menu.
 */
export function SidebarUserMenu({
  displayName,
  email,
  firstName,
  lastName,
}: SidebarUserMenuProps) {
  const navigate = useNavigate()
  const basePath = useBasePath()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const fullName =
    displayName ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    'Signed in'
  const initials = (() => {
    if (firstName || lastName) {
      return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U'
    }
    return (displayName?.[0] || 'U').toUpperCase()
  })()

  // Close on Escape while open, and return focus to the trigger.
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const handleAccount = () => {
    setIsOpen(false)
    navigate(`${basePath}/profile`)
  }

  const handleLogout = () => {
    setIsOpen(false)
    navigate('/auth/logout')
  }

  return (
    <div className="px-3 py-4 border-t border-nav-divider relative">
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
        <div className="h-8 w-8 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-medium text-[var(--brand-primary-text)]">
            {initials}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-nav truncate">{fullName}</p>
          {email && (
            <p className="text-xs text-nav-inactive truncate">{email}</p>
          )}
        </div>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(open => !open)}
          className="p-1.5 rounded-lg text-nav-inactive hover:text-nav hover:bg-nav-hover transition-colors"
          aria-label="Open account menu"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>

      {isOpen && (
        <>
          {/* Outside-click catcher — matches the mockup pattern. */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            aria-label="Account menu"
            className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
          >
            {/* Popover header — repeats the user identity. */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-[var(--brand-primary-text)]">
                  {initials}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-gray-900 text-sm font-medium truncate">
                  {fullName}
                </p>
                {email && (
                  <p className="text-gray-500 text-xs truncate">{email}</p>
                )}
              </div>
            </div>
            <div className="py-1">
              <button
                type="button"
                role="menuitem"
                onClick={handleAccount}
                className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors w-full text-left text-sm"
              >
                <User className="h-4 w-4" />
                <span>Account</span>
              </button>
            </div>
            <div className="border-t border-gray-100 py-1">
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors w-full text-left text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
